"use client";

// Auto-advancing horizontal benefits carousel (Figma 11892:39129 desktop /
// 7286:51345 mobile / 8184:57503 SBL). Embla loop + wall-clock autoplay —
// fixed height band, no vertical scroll coupling.

import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { SectionRevealHeading } from "@/components/blocks/section-reveal-heading";
import { SegmentedProgressBar } from "@/components/ui/segmented-progress-bar";
import { cn } from "@/lib/utils";

/** Desktop column — Figma 11892:39129 Benefits frames. */
const COL_W = 360;
const COL_H = 418;
/** Mobile column — careers Figma 7286:51345. */
const COL_W_MOBILE = 351;
const COL_H_MOBILE = 578;

const LOADER_TICKS = 44;
/** Duplicate slides so Embla loop works on wide viewports (canLoop guard). */
const LOOP_COPIES = 3;
/**
 * 4.5s per card — matches the company-history carousel (`AUTOPLAY_MS` in
 * components/about-us/company-history.tsx) so the site's card carousels
 * auto-advance at one cadence. Supersedes the 2–3s range in WEB-1096.
 */
const AUTOPLAY_MS = 4500;
/** Embla scrollTo direction: -1 = forward (matches scrollNext). */
const SCROLL_FORWARD = -1;
/** Strong ease-out — design-eng: responsive UI motion, <300ms */
const CARD_EASE = "ease-[cubic-bezier(0.23,1,0.32,1)]";
const CARD_MOTION_MS = "duration-300";

export interface ScrollDrivenCarouselCard {
  title: string;
  description: string;
  /** Icon well contents; receives whether this card is in the active (left) slot. */
  renderIcon: (isActive: boolean) => ReactNode;
}

/**
 * `section` keeps SBL's compact left-aligned heading; `display` matches the
 * centered scroll-revealed band titles on /careers.
 */
type CarouselTitleVariant = "section" | "display";

function CarouselTitle({
  title,
  variant,
}: {
  title: string;
  variant: CarouselTitleVariant;
}) {
  if (variant === "display") {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-6 pt-10 pb-6 md:px-8 md:pt-16 md:pb-8 lg:px-20 lg:pt-20 lg:pb-10">
        <SectionRevealHeading>{title}</SectionRevealHeading>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-6 pt-10 pb-6 md:px-8 md:pt-16 md:pb-8 lg:px-20 lg:pt-20 lg:pb-10">
      <h2 className="text-grey-950 text-ds-heading-h4 md:text-[28px] lg:text-[32px]">
        {title}
      </h2>
    </div>
  );
}

export interface ScrollDrivenHorizontalCarouselProps {
  cards: ScrollDrivenCarouselCard[];
  className?: string;
  /** Optional heading above the band (SBL, careers). */
  title?: string;
  /** Heading treatment — defaults to SBL's compact section style. */
  titleVariant?: CarouselTitleVariant;
  dataTestId?: string;
  dataFigmaSection?: string;
}

export function ScrollDrivenHorizontalCarousel({
  cards,
  className,
  title,
  titleVariant = "section",
  dataTestId = "scroll-driven-horizontal-carousel",
  dataFigmaSection,
}: ScrollDrivenHorizontalCarouselProps) {
  const reduceMotion = usePrefersReducedMotion();

  if (!cards.length) {
    return null;
  }

  if (reduceMotion) {
    return (
      <StaticBand
        cards={cards}
        title={title}
        titleVariant={titleVariant}
        className={className}
        dataTestId={dataTestId}
        dataFigmaSection={dataFigmaSection}
      />
    );
  }

  return (
    <div
      className={cn("bg-grey-50", className)}
      data-testid={dataTestId}
      data-figma-section={dataFigmaSection}
    >
      {title ? <CarouselTitle title={title} variant={titleVariant} /> : null}
      <EmblaBenefitsBand
        cards={cards}
        colWidth={COL_W_MOBILE}
        colHeight={COL_H_MOBILE}
        layout="mobile"
        className="md:hidden"
      />
      <EmblaBenefitsBand
        cards={cards}
        colWidth={COL_W}
        colHeight={COL_H}
        layout="desktop"
        className="hidden md:block"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Embla loop + wall-clock autoplay (company-history pattern).         */
/* ------------------------------------------------------------------ */

type TrackSlide = {
  card: ScrollDrivenCarouselCard;
  key: string;
  logicalIndex: number;
};

function buildTrackSlides(cards: ScrollDrivenCarouselCard[]): TrackSlide[] {
  return Array.from({ length: LOOP_COPIES }, (_, copy) =>
    cards.map((card, index) => ({
      card,
      key: `${card.title}-${copy}`,
      logicalIndex: index,
    }))
  ).flat();
}

function EmblaBenefitsBand({
  cards,
  colWidth,
  colHeight,
  layout,
  className,
}: {
  cards: ScrollDrivenCarouselCard[];
  colWidth: number;
  colHeight: number;
  layout: "desktop" | "mobile";
  className?: string;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const inViewRef = useRef(false);
  const draggingRef = useRef(false);
  const correctingRef = useRef(false);
  const trackSlides = useMemo(() => buildTrackSlides(cards), [cards]);
  const startIndex = cards.length;
  const lastPhysicalRef = useRef(startIndex);
  const [selectedSnap, setSelectedSnap] = useState(startIndex);
  const [progress, setProgress] = useState(0);

  const selectedIndex = selectedSnap % cards.length;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    containScroll: false,
    skipSnaps: false,
    dragFree: false,
    startIndex,
  });

  const scrollToLogicalIndex = useCallback(
    (logicalIndex: number) => {
      if (!emblaApi || cards.length === 0) return;

      const len = cards.length;
      const target = ((logicalIndex % len) + len) % len;
      const currentPhysical = emblaApi.selectedScrollSnap();
      const currentLogical = currentPhysical % len;
      if (target === currentLogical) return;

      const forwardSteps = (target - currentLogical + len) % len;
      (
        emblaApi.scrollTo as (
          index: number,
          jump?: boolean,
          direction?: number
        ) => void
      )(currentPhysical + forwardSteps, false, SCROLL_FORWARD);
    },
    [emblaApi, cards.length]
  );

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = entry?.isIntersecting ?? false;
      },
      { threshold: 0.35 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const physical = emblaApi.selectedScrollSnap();
      const total = trackSlides.length;
      const prev = lastPhysicalRef.current;

      if (!correctingRef.current && draggingRef.current && total > 0) {
        const forward = (physical - prev + total) % total;
        const backward = (prev - physical + total) % total;
        if (backward > 0 && backward < forward) {
          correctingRef.current = true;
          emblaApi.scrollNext();
          correctingRef.current = false;
          return;
        }
      }

      lastPhysicalRef.current = physical;
      setSelectedSnap(physical);
    };

    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, trackSlides.length]);

  // Single timer drives progress fill and scrollNext — keeps them in sync.
  useEffect(() => {
    if (!emblaApi) return;

    let elapsed = 0;
    let last = performance.now();

    const onSelectReset = () => {
      elapsed = 0;
      setProgress(0);
    };
    const onPointerDown = () => {
      draggingRef.current = true;
    };
    const onResume = () => {
      draggingRef.current = false;
    };

    emblaApi.on("select", onSelectReset);
    emblaApi.on("pointerDown", onPointerDown);
    emblaApi.on("pointerUp", onResume);
    emblaApi.on("settle", onResume);

    const id = window.setInterval(() => {
      const now = performance.now();
      const dt = Math.min(now - last, 200);
      last = now;

      if (draggingRef.current || document.hidden || !inViewRef.current) return;

      elapsed += dt;
      if (elapsed >= AUTOPLAY_MS) {
        elapsed = 0;
        emblaApi.scrollNext();
      }

      setProgress(Math.min(elapsed / AUTOPLAY_MS, 1));
    }, 50);

    return () => {
      window.clearInterval(id);
      emblaApi.off("select", onSelectReset);
      emblaApi.off("pointerDown", onPointerDown);
      emblaApi.off("pointerUp", onResume);
      emblaApi.off("settle", onResume);
    };
  }, [emblaApi]);

  return (
    <section
      ref={sectionRef}
      data-band={layout}
      className={cn("relative", className)}
      style={{ height: colHeight }}
    >
      <div
        className="relative h-full cursor-grab overflow-hidden active:cursor-grabbing"
        ref={emblaRef}
      >
        <div className="flex h-full touch-pan-y touch-pinch-zoom">
          {trackSlides.map((slide, index) => {
            const isActive = index === selectedSnap;
            return (
              <div
                key={slide.key}
                className="h-full shrink-0 grow-0"
                style={{ flexBasis: colWidth, width: colWidth }}
              >
                <CarouselSlot
                  card={slide.card}
                  isActive={isActive}
                  progress={isActive ? progress : 0}
                  width={colWidth}
                  height={colHeight}
                  layout={layout}
                  onSelect={
                    isActive
                      ? undefined
                      : () => scrollToLogicalIndex(slide.logicalIndex)
                  }
                />
              </div>
            );
          })}
        </div>
      </div>

      <p className="sr-only" aria-live="polite">
        Benefit {selectedIndex + 1} of {cards.length}:{" "}
        {cards[selectedIndex]?.title}
      </p>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Reduced-motion: static multi-column snapshot (first card active).   */
/* ------------------------------------------------------------------ */

function StaticBand({
  cards,
  title,
  titleVariant = "section",
  className,
  dataTestId,
  dataFigmaSection,
}: {
  cards: ScrollDrivenCarouselCard[];
  title?: string;
  titleVariant?: CarouselTitleVariant;
  className?: string;
  dataTestId?: string;
  dataFigmaSection?: string;
}) {
  return (
    <section
      data-testid={dataTestId}
      data-figma-section={dataFigmaSection}
      className={cn("bg-grey-50 relative", className)}
    >
      {title ? <CarouselTitle title={title} variant={titleVariant} /> : null}

      <div
        className="relative hidden w-full overflow-hidden md:block"
        style={{ height: COL_H }}
      >
        <div className="relative flex h-full">
          {cards.map((card, index) => (
            <CarouselSlot
              key={card.title}
              card={card}
              isActive={index === 0}
              progress={index === 0 ? 0.25 : 0}
              width={COL_W}
              height={COL_H}
              layout="desktop"
            />
          ))}
        </div>
      </div>

      <div
        className="relative w-full overflow-hidden md:hidden"
        style={{ height: COL_H_MOBILE }}
      >
        <div className="relative flex h-full">
          {cards.map((card, index) => (
            <CarouselSlot
              key={card.title}
              card={card}
              isActive={index === 0}
              progress={index === 0 ? 0.25 : 0}
              width={COL_W_MOBILE}
              height={COL_H_MOBILE}
              layout="mobile"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CarouselSlot({
  card,
  isActive,
  progress,
  width,
  height,
  layout,
  onSelect,
  className,
}: {
  card: ScrollDrivenCarouselCard;
  isActive: boolean;
  progress: number;
  width: number;
  height: number;
  layout: "desktop" | "mobile";
  onSelect?: () => void;
  className?: string;
}) {
  if (layout === "desktop") {
    return (
      <DesktopColumn
        card={card}
        isActive={isActive}
        progress={progress}
        width={width}
        height={height}
        onSelect={onSelect}
        className={className}
      />
    );
  }

  return (
    <div
      className={cn(
        "border-grey-100 relative h-full shrink-0 border-t border-r border-b",
        "transition-colors duration-500 ease-out motion-reduce:transition-none",
        isActive ? "bg-grey-950" : "bg-grey-50",
        className
      )}
      style={{ width, height }}
    >
      <BenefitCard
        card={card}
        isActive={isActive}
        progress={progress}
        onSelect={onSelect}
        className={cn(
          "absolute bottom-[34px] origin-bottom-left",
          isActive ? "left-[26px] w-[300px]" : "left-[15px] w-[328px]"
        )}
      />
    </div>
  );
}

function CardSelectOverlay({
  label,
  onSelect,
}: {
  label: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onSelect}
      className="focus-visible:ring-pulse-700 absolute inset-0 z-10 cursor-pointer rounded-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
    />
  );
}

function DesktopColumn({
  card,
  isActive,
  progress,
  width,
  height,
  onSelect,
  className,
}: {
  card: ScrollDrivenCarouselCard;
  isActive: boolean;
  progress: number;
  width: number;
  height: number;
  onSelect?: () => void;
  className?: string;
}) {
  return (
    <article
      aria-current={isActive ? "true" : undefined}
      className={cn(
        "border-grey-100 relative flex h-full shrink-0 flex-col border-t border-r border-b p-12",
        "transition-colors duration-500 ease-out motion-reduce:transition-none",
        isActive ? "bg-grey-950 justify-between" : "bg-grey-50",
        onSelect && "cursor-pointer",
        className
      )}
      style={{ width, height }}
    >
      {onSelect ? (
        <CardSelectOverlay
          label={`View benefit: ${card.title}`}
          onSelect={onSelect}
        />
      ) : null}
      <div className="flex min-h-0 flex-col gap-4">
        <div
          className={cn(
            "flex size-10 items-center justify-center rounded-xl",
            CARD_MOTION_MS,
            CARD_EASE,
            "transition-colors motion-reduce:transition-none",
            isActive ? "bg-grey-800" : "bg-grey-200"
          )}
        >
          <span className="flex size-6 items-center justify-center">
            {card.renderIcon(isActive)}
          </span>
        </div>
        <h3
          className={cn(
            "text-[20px] leading-[1.3] tracking-[-0.8px]",
            CARD_MOTION_MS,
            CARD_EASE,
            "transition-colors motion-reduce:transition-none",
            isActive ? "text-grey-0" : "text-grey-950"
          )}
        >
          {card.title}
        </h3>
        <p
          className={cn(
            "text-[12px] leading-[1.4] tracking-[-0.24px]",
            CARD_MOTION_MS,
            CARD_EASE,
            "transition-colors motion-reduce:transition-none",
            isActive ? "text-grey-500" : "text-grey-700"
          )}
        >
          {card.description}
        </p>
      </div>

      {isActive ? (
        <SegmentedProgressBar
          progress={progress}
          tickCount={LOADER_TICKS}
          className="shrink-0 [&_span]:transition-colors [&_span]:duration-150"
        />
      ) : null}
    </article>
  );
}

function BenefitCard({
  card,
  className,
  isActive,
  progress,
  onSelect,
}: {
  card: ScrollDrivenCarouselCard;
  className?: string;
  isActive: boolean;
  progress: number;
  onSelect?: () => void;
}) {
  return (
    <article
      aria-current={isActive ? "true" : undefined}
      className={cn(
        "rounded-ds-xl relative flex origin-bottom-left flex-col p-6",
        CARD_MOTION_MS,
        CARD_EASE,
        "motion-reduce:transition-none",
        "transition-[transform,background-color,gap]",
        isActive
          ? "bg-grey-900 scale-100 gap-[110px]"
          : "bg-grey-100 scale-[0.96] gap-0",
        onSelect && "cursor-pointer",
        className
      )}
    >
      {onSelect ? (
        <CardSelectOverlay
          label={`View benefit: ${card.title}`}
          onSelect={onSelect}
        />
      ) : null}
      <div className="flex flex-col gap-3">
        <div
          className={cn(
            "flex size-10 items-center justify-center rounded-xl",
            CARD_MOTION_MS,
            CARD_EASE,
            "transition-colors motion-reduce:transition-none",
            isActive ? "bg-grey-800" : "bg-grey-200"
          )}
        >
          <span className="flex size-6 items-center justify-center">
            {card.renderIcon(isActive)}
          </span>
        </div>
        <h3
          className={cn(
            "text-[20px] leading-[1.3] tracking-[-0.8px]",
            CARD_MOTION_MS,
            CARD_EASE,
            "transition-colors motion-reduce:transition-none",
            isActive ? "text-grey-0" : "text-grey-950"
          )}
        >
          {card.title}
        </h3>
        <p
          className={cn(
            "text-[12px] leading-[1.4] tracking-[-0.24px]",
            CARD_MOTION_MS,
            CARD_EASE,
            "transition-colors motion-reduce:transition-none",
            isActive ? "text-grey-500" : "text-grey-700"
          )}
        >
          {card.description}
        </p>
      </div>

      {isActive ? (
        <SegmentedProgressBar
          progress={progress}
          tickCount={LOADER_TICKS}
          className="shrink-0 [&_span]:transition-colors [&_span]:duration-150"
        />
      ) : null}
    </article>
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return reduced;
}
